import { useState } from 'react';
import InputBox from '../../../components/Input/InputBox';
import BaseButton from '../../../components/common/BaseButton';
import IconButton from '../../../components/common/IconButton';
import api from '../../../utils/api';
import {
  Box,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { formikErrorInput, formikErrorMessage } from '../../../utils/helper';
import PropTypes from 'prop-types';
import { getCookie, removeCookie, setCookie } from '../../../utils/cookies';
import { useConfig } from '../../../Hooks/useConfig';
import { useAuthStatus } from '../../../store/auth/useAuthStatus';
import useUsersFacade from '../../../store/profile/useUsersFacade';

const CenteredMailIcon = styled('img')(({ theme }) => ({
  width: 64,
  height: 64,
  display: 'block',
  margin: '0 auto',
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(6),
  },
}));

const CheckEmailHeading = styled(Typography)(({ theme }) => ({
  fontSize: `${theme.spacing(7)} !important`,
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: theme.spacing(9),
  textAlign: 'center',
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(6),
    fontSize: `${theme.spacing(5)} !important`,
    fontWeight: 500,
    lineHeight: theme.spacing(7),
  },
}));

const EmailInstructionText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontSize: `${theme.spacing(3.5)} !important`,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: `${theme.spacing(6.25)}px`,
  color: '#1A1B1F',
  textAlign: 'center',
  marginBottom: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(6),
    fontSize: `${theme.spacing(3.5)} !important`,
    lineHeight: `${theme.spacing(6.25)}px`,
  },
}));

const EmailText = styled('span')(({ theme }) => ({
  fontFamily: 'Poppins',
  fontSize: `${theme.spacing(4)} !important`,
  fontStyle: 'normal',
  fontWeight: 600,
  color: '#1A1B1F',
  [theme.breakpoints.down('md')]: {
    fontSize: `${theme.spacing(3.5)} !important`,
    lineHeight: `${theme.spacing(5)}px`,
  },
}));

const ResendLink = styled('span')(({ theme, disabled }) => ({
  fontFamily: 'Poppins',
  fontSize: `${theme.spacing(3.75)} !important`,
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: `${theme.spacing(6.25)}px`,
  color: disabled ? '#9E9E9E' : '#0486D7',
  textDecoration: 'underline',
  textDecorationStyle: 'solid',
  cursor: disabled ? 'default' : 'pointer',
  pointerEvents: disabled ? 'none' : 'auto',
  '&:hover': {
    opacity: disabled ? 1 : 0.8,
  },
  [theme.breakpoints.down('md')]: {
    fontSize: `${theme.spacing(3.75)} !important`,
    lineHeight: `${theme.spacing(6.25)}px`,
  },
}));

const ConfirmEmailContent = ({ onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [sucsses, setSucsses] = useState(false);
  const { userInfo, loading } = useUsersFacade();

  const { t } = useTranslation();
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { baseDomain } = useConfig();

  const { setToken } = useAuthStatus();

  const formik = useFormik({
    initialValues: {
      email: '',
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .email(t('login.inValid_email'))
        .required(t('forgot_password.required_email')),
    }),

    onSubmit: (values) => {
      forgetPasswordEmail(values);
    },
  });

  const forgetPasswordEmail = async (values) => {
    setIsLoading(true);
    const forgetpasswordReq = await api.call({
      name: 'forgetPassword',
      data: {
        email: values.email.toLowerCase(),
      },
    });
    if (forgetpasswordReq.status === 200) {
      setSucsses(true);
      onSuccess && onSuccess();
    }
    setIsLoading(false);
  };

  const resendEmail = async () => {
    setIsLoading(true);
    const resendEmailReq = await api.call({
      name: 'resendEmail',
      data: {
        email: userInfo.email,
      },
    });
    setIsLoading(false);
  };

  return (
    <>
      <CenteredMailIcon src="/static/img/icon/mail-icon-light.png" alt="" />
      <CheckEmailHeading>Please verify your email</CheckEmailHeading>
      <EmailInstructionText>
        If you don’t see the email, <EmailText>check your spam </EmailText>
        folder. Still can’t find the email?{' '}
        <ResendLink
          disabled={isLoading}
          onClick={() => resendEmail(formik.values)}
        >
          Resend email
        </ResendLink>
      </EmailInstructionText>

      <Box
        sx={{
          display: 'flex',
          gap: theme.spacing(8),
          marginTop: theme.spacing(6),
          justifyContent: 'center',
          paddingBottom: theme.spacing(4),
          [theme.breakpoints.down('md')]: {
            marginTop: theme.spacing(4),
          },
        }}
      >
        <IconButton
          text="Open Gmail"
          icon="/static/img/icon/gmail-icon.png"
          onClick={() => window.open('https://mail.google.com', '_blank')}
        />
        <IconButton
          text="Open Outlook"
          icon="/static/img/icon/outlook-icon.png"
          onClick={() => window.open('https://outlook.live.com', '_blank')}
        />
      </Box>
    </>
  );
};

ConfirmEmailContent.propTypes = {
  onSuccess: PropTypes.func,
};

export default ConfirmEmailContent;
